import { Link, useParams } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import { comics } from "@/mocks/comics";
import ComicCard from "@/components/feature/ComicCard";

export default function Detail() {
  const { id } = useParams();
  const comic = comics.find((c) => c.id === id);

  usePageMeta({
    title: comic
      ? `《${comic.title}》${comic.category}韩国漫画-在线详情介绍 - 羞羞漫画韩国`
      : "漫画详情 - 羞羞漫画韩国",
    description: comic
      ? `${comic.intro} ${comic.title}是${comic.author}创作的${comic.category}题材韩国漫画，当前${comic.status}，已更新${comic.chapters}话，评分${comic.rating}分。`
      : "羞羞漫画韩国漫画详情介绍",
  });

  if (!comic) {
    return (
      <main className="flex min-h-[50vh] w-full flex-col items-center justify-center bg-background-50 px-4 py-20 text-center">
        <i className="ri-book-2-line text-4xl text-foreground-300" aria-hidden="true"></i>
        <p className="mt-4 text-sm text-foreground-600">没有找到这部作品，它可能已经被移除了。</p>
        <Link
          to="/category"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
        >
          返回分类页
        </Link>
      </main>
    );
  }

  const related = comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 4);

  return (
    <main className="w-full bg-background-50">
      {/* Banner */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={comic.banner} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-950/55 to-background-950/60"></div>
        </div>
        <div className="relative mx-auto max-w-7xl px-4 pb-10 pt-14 md:px-6 md:pt-20">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-background-100" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-accent-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <Link to="/category" className="transition-colors hover:text-accent-300">
              题材分类
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <Link
              to={`/category?type=${encodeURIComponent(comic.category)}`}
              className="transition-colors hover:text-accent-300"
            >
              {comic.category}
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="text-background-50">{comic.title}</span>
          </nav>

          <div className="mt-8 flex flex-col gap-6 md:flex-row md:items-end">
            <img
              src={comic.cover}
              alt={`${comic.title} 封面`}
              title={`${comic.title} · 韩国${comic.category}漫画`}
              className="h-72 w-52 shrink-0 rounded-xl object-cover object-top ring-4 ring-background-50/20"
            />
            <div className="max-w-2xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-medium text-background-50">
                  {comic.category}
                </span>
                <span className="rounded-md bg-background-50/20 px-2.5 py-1 text-xs font-medium text-background-50 backdrop-blur">
                  {comic.status}
                </span>
                <span className="flex items-center gap-1 text-sm font-semibold text-accent-300">
                  <i className="ri-star-fill" aria-hidden="true"></i>
                  {comic.rating.toFixed(1)}
                </span>
              </div>
              <h1 className="mt-3 font-display text-3xl font-black text-background-50 md:text-4xl">
                {comic.title}
              </h1>
              <p className="mt-2 text-sm text-background-200">
                作者 · {comic.author}　|　{comic.views} 人气　|　{comic.chapters} 话
              </p>
              <p className="mt-4 text-sm leading-relaxed text-background-100">{comic.intro}</p>
              <div className="mt-5 flex flex-wrap items-center gap-2">
                {comic.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-md bg-background-50/15 px-2.5 py-1 text-xs text-background-100 backdrop-blur"
                  >
                    #{t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 内容主体 */}
      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="rounded-xl bg-background-100 p-6 md:p-8">
              <h2 className="flex items-center gap-2 font-display text-xl font-black text-foreground-950">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className="ri-quill-pen-line text-base" aria-hidden="true"></i>
                </span>
                作品简介
              </h2>
              <p className="mt-4 text-sm leading-7 text-foreground-700">{comic.synopsis}</p>
            </div>

            <div className="mt-8 rounded-xl bg-background-100 p-6 md:p-8">
              <h2 className="flex items-center gap-2 font-display text-xl font-black text-foreground-950">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-500 text-background-50">
                  <i className="ri-team-line text-base" aria-hidden="true"></i>
                </span>
                主要角色
              </h2>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {comic.characters.map((ch) => (
                  <div key={ch.name} className="rounded-lg bg-background-50 p-4 ring-1 ring-background-200/70">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 font-display text-base font-bold text-primary-700">
                        {ch.name.charAt(0)}
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-foreground-950">{ch.name}</p>
                        <p className="text-xs text-foreground-500">{ch.role}</p>
                      </div>
                    </div>
                    <p className="mt-3 text-xs leading-relaxed text-foreground-600">{ch.intro}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 rounded-xl bg-background-100 p-6 md:p-8">
              <h2 className="flex items-center gap-2 font-display text-xl font-black text-foreground-950">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-500 text-background-50">
                  <i className="ri-list-unordered text-base" aria-hidden="true"></i>
                </span>
                最近章节
              </h2>
              <ul className="mt-5 space-y-2">
                {comic.chapterList.map((chap) => (
                  <li key={chap.id}>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-3 rounded-md bg-background-50 px-4 py-2.5 text-left ring-1 ring-background-200/70 transition-colors hover:ring-primary-300"
                    >
                      <span className="text-sm text-foreground-800">
                        第 {chap.num} 话 · {chap.title}
                      </span>
                      <span className="flex items-center gap-2 text-xs text-foreground-400">
                        <i className="ri-eye-line" aria-hidden="true"></i>
                        {chap.views}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <p className="mt-4 rounded-md bg-secondary-100 px-4 py-3 text-xs leading-relaxed text-secondary-900">
                说明：本站为内容介绍与推荐站，不提供直接阅读。请通过正规版权平台支持创作者。
              </p>
            </div>
          </div>

          {/* 侧边推荐 */}
          <aside className="space-y-8" aria-label="相关推荐">
            <div className="rounded-xl bg-background-100 p-5">
              <h2 className="font-display text-base font-black text-foreground-950">同类推荐</h2>
              <div className="mt-4 space-y-3">
                {related.length > 0 ? (
                  related.map((c) => (
                    <Link
                      key={c.id}
                      to={`/detail/${c.id}`}
                      className="group flex items-center gap-3 rounded-lg bg-background-50 p-2 ring-1 ring-background-200/70 transition-all hover:ring-primary-300"
                    >
                      <img
                        src={c.cover}
                        alt=""
                        className="h-14 w-10 shrink-0 rounded-md object-cover object-top"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-foreground-950 group-hover:text-primary-600">
                          {c.title}
                        </p>
                        <p className="mt-0.5 text-xs text-foreground-500">
                          {c.chapters}话 · {c.rating.toFixed(1)}分
                        </p>
                      </div>
                    </Link>
                  ))
                ) : (
                  <p className="text-sm text-foreground-500">暂无同类作品。</p>
                )}
              </div>
            </div>

            <div className="rounded-xl bg-accent-100/70 p-5">
              <h2 className="flex items-center gap-2 font-display text-base font-black text-foreground-950">
                <i className="ri-lightbulb-flash-line text-accent-600" aria-hidden="true"></i>
                小贴士
              </h2>
              <p className="mt-3 text-xs leading-relaxed text-foreground-700">
                想看更多同题材佳作？前往实时榜单与分类页，人气榜与编辑精选都在等你发现。
              </p>
              <Link
                to="/rankings"
                className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-accent-700 hover:text-accent-800"
              >
                去榜单逛逛 <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* 相关推荐卡片 */}
      {related.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 pb-10 md:px-6">
          <h2 className="font-display text-xl font-black text-foreground-950">同类题材精选</h2>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </section>
      )}

      {/* Article 结构化数据 */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: comic.title,
            description: comic.intro,
            author: { "@type": "Person", name: comic.author },
            keywords: [comic.category, ...comic.tags].join(","),
            datePublished: "2026-01-01",
            inLanguage: "zh-CN",
            mainEntityOfPage: `https://koreope.github.io/detail/${comic.id}`,
          }),
        }}
      />
    </main>
  );
}